
import { useState, useEffect } from "react";
import { Product, Group } from "@/lib/types";
import { generateId } from "@/lib/storage";
import { fetchProducts } from "@/lib/storage/product/api";
import { useToast } from "@/hooks/use-toast";
import { GroupFormData } from "./GroupFormContent";
import { ProductQuantity } from "./GroupProductQuantities";

interface UseGroupFormProps {
  initialGroup?: Group;
  onSave: (group: Group, quantities: ProductQuantity[]) => void;
}

export const useGroupForm = ({ initialGroup, onSave }: UseGroupFormProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [group, setGroup] = useState<Partial<Group>>({
    id: initialGroup?.id || generateId(),
    name: initialGroup?.name || "",
    description: initialGroup?.description || "",
    productIds: initialGroup?.productIds || [],
    createdAt: initialGroup?.createdAt || new Date(),
    updatedAt: initialGroup?.updatedAt || new Date(),
  });
  const [productQuantities, setProductQuantities] = useState<ProductQuantity[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      try {
        console.log("Fetching products for group form...");
        const fetchedProducts = await fetchProducts();
        console.log("Retrieved", fetchedProducts.length, "products for group form");
        setProducts(fetchedProducts);
      } catch (error) {
        console.error("Error loading products:", error);
        toast({
          title: "Error",
          description: "Failed to load products. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
    
    if (initialGroup) {
      console.log("Setting initial group data:", initialGroup);
      setGroup({
        id: initialGroup.id,
        name: initialGroup.name,
        description: initialGroup.description,
        productIds: initialGroup.productIds,
        createdAt: initialGroup.createdAt,
        updatedAt: new Date(),
      });
    }
  }, [initialGroup, toast]);

  const handleFormDataChange = (formData: GroupFormData) => {
    setGroup((prev) => ({
      ...prev,
      name: formData.name,
      description: formData.description,
      productIds: formData.productIds,
    }));
    
    if (formData.productQuantities && formData.productQuantities.length > 0) {
      console.log("Updating product quantities in useGroupForm:", formData.productQuantities);
      setProductQuantities(formData.productQuantities);
    }
  };

  const handleSubmit = async () => {
    if (!group.name) {
      toast({
        title: "Missing Information",
        description: "Please provide a name for the group.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      const completeGroup: Group = {
        id: group.id || generateId(),
        name: group.name,
        description: group.description || "",
        productIds: group.productIds || [],
        createdAt: group.createdAt || new Date(),
        updatedAt: new Date(),
      };
      
      console.log("Saving group:", completeGroup);
      console.log("With quantities:", productQuantities);
      await onSave(completeGroup, productQuantities);
    } catch (error) {
      console.error("Error saving group:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the group.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    products,
    group,
    isLoading,
    isSubmitting,
    handleFormDataChange,
    handleSubmit,
  };
};
